import { useMemo, useState } from 'react';
import { ExternalLink, FileStack, Link2, Loader2, Search, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MEDIA_CLASS_BADGE,
  MEDIA_SENTIMENT_BADGE,
  MEDIA_STATUS_BADGE,
} from '@/client/types/media';
import type { MediaChannel, MediaItemStatus, MediaKind } from '@/client/types/media';
import {
  useAddBriefItemsMutation,
  useBriefsQuery,
  useBulkTriageMutation,
  useCoverageQuery,
  useIngestUrlMutation,
  useTriageCoverageMutation,
} from '@/client/api/marketing/radar';
import type { ApiCoverageItem } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import { publishedRangeParams } from '../coverage-api';

/**
 * The shared coverage table, on real data.
 *
 * Two things it now says out loud that the mock could not:
 *
 * - **`sentiment: 'unknown'` is "not classified", not neutral.** A classifier
 *   runs on a 10-minute beat; until it does, the value is honestly absent.
 *   Rendering it as neutral would turn the sentiment mix into a chart of how
 *   much classification is outstanding.
 *
 * - **An unreachable API is not an empty archive.** Both look like an empty
 *   table, and only one of them means "nothing has been said about us".
 *
 * Triaged items can be added straight to a brief, which is what the brief-item
 * endpoints exist for — triage and assembly are the same sitting.
 */

export interface CoverageListFilters {
  channel?: MediaChannel;
  kind?: MediaKind | MediaKind[];
  status?: MediaItemStatus | 'all';
  defaultStatus?: MediaItemStatus | 'all';
  inboxMode?: boolean;
}

interface CoverageListProps {
  filters: CoverageListFilters;
  emptyHint?: string;
}

const SENTIMENT_LABEL: Record<string, string> = {
  positive: 'positive',
  neutral: 'neutral',
  negative: 'negative',
  unknown: 'not classified',
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

function matchesKind(item: ApiCoverageItem, kind?: MediaKind | MediaKind[]) {
  if (!kind) return true;
  return Array.isArray(kind)
    ? kind.includes(item.kind as MediaKind)
    : item.kind === kind;
}

/** Where the item came from, whichever of the three fields is populated. */
const sourceLabel = (item: ApiCoverageItem) =>
  item.outlet_name ?? item.site_name ?? item.channel ?? '—';

export function CoverageList({
  filters,
  emptyHint = 'No coverage items match these filters.',
}: CoverageListProps) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<MediaItemStatus | 'all'>(
    filters.status ?? filters.defaultStatus ?? (filters.inboxMode ? 'new' : 'all'),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState<ApiCoverageItem | null>(null);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [ingestUrl, setIngestUrl] = useState('');
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefId, setBriefId] = useState('');

  const { data, isLoading, isError, error } = useCoverageQuery({
    limit: 500,
    status: status === 'all' ? undefined : status,
    channel: filters.channel,
    // The API takes one kind; an array is filtered client-side below.
    kind: typeof filters.kind === 'string' ? filters.kind : undefined,
    ...publishedRangeParams(90),
  });
  const briefs = useBriefsQuery({ limit: 100, status: 'draft' });
  const triage = useTriageCoverageMutation();
  const bulkTriage = useBulkTriageMutation();
  const ingest = useIngestUrlMutation();
  const addToBrief = useAddBriefItemsMutation();

  const items: ApiCoverageItem[] = data?.items ?? [];

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return items
      .filter((item) => (filters.channel ? item.channel === filters.channel : true))
      .filter((item) => matchesKind(item, filters.kind))
      .filter((item) => {
        if (!query) return true;
        return (
          item.title.toLowerCase().includes(query) ||
          (item.summary ?? '').toLowerCase().includes(query) ||
          (item.author?.toLowerCase().includes(query) ?? false) ||
          (item.outlet_name?.toLowerCase().includes(query) ?? false)
        );
      })
      .sort(
        (a, b) =>
          (b.urgency_score ?? 0) - (a.urgency_score ?? 0) ||
          (b.published_at ?? '').localeCompare(a.published_at ?? ''),
      );
  }, [items, filters.channel, filters.kind, q]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.id)));
  };

  const bulk = (next: MediaItemStatus) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulkTriage.mutate(
      { ids, status: next },
      {
        onSuccess: (res) => {
          const modified = res?.data?.modified ?? 0;
          // Requested and modified differ when something was already in that
          // state. Reporting the request count would overstate the work.
          toast.success(`${modified} of ${ids.length} moved to ${next}`);
          setSelected(new Set());
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not triage the selection')),
      },
    );
  };

  const submitIngest = () => {
    const url = ingestUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Paste a full http(s) URL');
      return;
    }
    ingest.mutate(
      { url },
      {
        onSuccess: (res) => {
          // Dedupe is on the canonical URL, so a link copied with tracking
          // parameters resolves to the story already in the archive.
          const existing = (res?.data?.seen_count ?? 0) > 1;
          toast.success(
            existing ? 'Already tracked — refreshed the existing item' : 'Coverage added',
          );
          setIngestUrl('');
          setIngestOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not ingest that URL')),
      },
    );
  };

  const submitAddToBrief = () => {
    if (!briefId) {
      toast.error('Pick a brief');
      return;
    }
    addToBrief.mutate(
      { mbid: Number(briefId), coverage_ids: [...selected] },
      {
        onSuccess: (res) => {
          const added = res?.data?.added ?? 0;
          const skipped = res?.data?.skipped ?? 0;
          toast.success(
            skipped > 0
              ? `${added} added, ${skipped} already in the brief`
              : `${added} added to the brief`,
          );
          setSelected(new Set());
          setBriefOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not add to the brief')),
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, author, outlet…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as MediaItemStatus | 'all')}
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="flagged">Flagged</option>
          <option value="archived">Archived</option>
        </select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setIngestOpen(true)}
        >
          <Link2 className="h-3.5 w-3.5" /> Add by URL
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span className="font-medium">{selected.size} selected</span>
          <Button size="sm" variant="secondary" onClick={() => bulk('reviewed')}>
            Mark reviewed
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('flagged')}>
            Flag
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('archived')}>
            Archive
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5"
            onClick={() => setBriefOpen(true)}
          >
            <FileStack className="h-3.5 w-3.5" /> Add to brief
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                {['Coverage', 'Source', 'Signal', 'Status', 'When'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading coverage…
                  </td>
                </tr>
              )}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm">
                    <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
                    <div className="font-medium text-amber-800">Could not load coverage</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {getApiErrorMessage(error, 'The marketing API did not respond')}
                    </div>
                  </td>
                </tr>
              )}

              {!isLoading && !isError && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-500">
                    {emptyHint}
                  </td>
                </tr>
              )}

              {filtered.map((item) => {
                const sentiment = item.sentiment ?? 'unknown';
                return (
                  <tr
                    key={item.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setActive(item)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggle(item.id)}
                        aria-label={`Select ${item.title}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-md text-sm font-medium text-gray-900">
                        {item.title}
                      </div>
                      {item.summary && (
                        <div className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                          {item.summary}
                        </div>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${
                            MEDIA_CLASS_BADGE[
                              item.media_class as keyof typeof MEDIA_CLASS_BADGE
                            ] ?? 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {item.media_class}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium capitalize text-gray-600">
                          {item.kind}
                        </span>
                        {(item.seen_count ?? 0) > 1 && (
                          <span
                            className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700"
                            title="Seen from more than one ingest source"
                          >
                            seen {item.seen_count}×
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{sourceLabel(item)}</div>
                      <div className="text-xs text-gray-400">{item.author ?? '—'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            MEDIA_SENTIMENT_BADGE[
                              sentiment as keyof typeof MEDIA_SENTIMENT_BADGE
                            ] ?? MEDIA_SENTIMENT_BADGE.unknown
                          }`}
                          title={
                            sentiment === 'unknown'
                              ? 'The classifier has not run on this item yet'
                              : undefined
                          }
                        >
                          {SENTIMENT_LABEL[sentiment] ?? sentiment}
                        </span>
                        {item.urgency_score != null && (
                          <span className="text-xs text-gray-500">urg {item.urgency_score}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                          MEDIA_STATUS_BADGE[item.status] ?? ''
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {fmtDate(item.published_at ?? item.captured_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
          {filtered.length} item{filtered.length === 1 ? '' : 's'}
          {data?.total != null && data.total > items.length
            ? ` of ${data.total} — narrow the filters to reach the rest`
            : ''}
        </div>
      </div>

      {/* Detail panel. Deliberately simple: triage plus a link out, which is
          what the row is opened for. */}
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{active.title}</DialogTitle>
                <DialogDescription>
                  {sourceLabel(active)}
                  {active.author ? ` · ${active.author}` : ''} ·{' '}
                  {fmtDate(active.published_at ?? active.captured_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 text-sm">
                {active.summary && <p className="text-gray-700">{active.summary}</p>}
                {active.body_excerpt && (
                  <p className="whitespace-pre-line rounded-md bg-gray-50 p-3 text-xs text-gray-600">
                    {active.body_excerpt}
                  </p>
                )}
                {active.url && (
                  <a
                    href={active.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline"
                  >
                    Read the original <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {active.sentiment === 'unknown' && (
                  <p className="rounded-md border border-amber-100 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                    Sentiment has not been classified yet. That is not the same as neutral — the
                    classifier runs on a schedule.
                  </p>
                )}
                {active.internal_note && (
                  <p className="text-xs text-gray-500">Note: {active.internal_note}</p>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2 sm:gap-2">
                {(['reviewed', 'flagged', 'archived'] as const).map((next) => (
                  <Button
                    key={next}
                    type="button"
                    variant={active.status === next ? 'default' : 'outline'}
                    size="sm"
                    disabled={active.status === next || triage.isPending}
                    onClick={() =>
                      triage.mutate(
                        { coverage_id: active.id, status: next },
                        {
                          onSuccess: () => {
                            toast.success(`Marked ${next}`);
                            setActive({ ...active, status: next });
                          },
                          onError: (err) =>
                            toast.error(getApiErrorMessage(err, 'Could not triage this item')),
                        },
                      )
                    }
                  >
                    {next === 'reviewed' ? 'Mark reviewed' : next === 'flagged' ? 'Flag' : 'Archive'}
                  </Button>
                ))}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={ingestOpen} onOpenChange={setIngestOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add coverage by URL</DialogTitle>
            <DialogDescription>
              The page is fetched and extracted server-side. Deduped on the publisher's canonical
              URL, so a link copied with tracking parameters resolves to the story already here
              rather than creating a second copy.
            </DialogDescription>
          </DialogHeader>
          <input
            value={ingestUrl}
            onChange={(e) => setIngestUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitIngest()}
            placeholder="https://…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setIngestOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submitIngest} disabled={ingest.isPending}>
              {ingest.isPending ? 'Fetching…' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {selected.size} item(s) to a brief</DialogTitle>
            <DialogDescription>
              Only draft briefs are listed — a published brief is a record of what went out, so
              adding to it after the fact would change history.
            </DialogDescription>
          </DialogHeader>
          <select
            value={briefId}
            onChange={(e) => setBriefId(e.target.value)}
            className="h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
          >
            <option value="">Select a brief…</option>
            {(briefs.data?.items ?? []).map((b) => (
              <option key={b.mbid} value={String(b.mbid)}>
                {b.title}
              </option>
            ))}
          </select>
          {(briefs.data?.items ?? []).length === 0 && !briefs.isLoading && (
            <p className="text-xs text-amber-700">
              No draft briefs. Create one under Radar → Briefs first.
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setBriefOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submitAddToBrief} disabled={addToBrief.isPending}>
              {addToBrief.isPending ? 'Adding…' : 'Add to brief'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
